import { ConsoleLog } from '@inntechcorp/it-design';
import { useEffect, useCallback, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import useAPI from 'Utils/api/useAPI';
import { Asset, CoreConfig } from 'Config/CoreConfig';
import { INNError } from 'Api/hooks/usePostFetch';

export interface RequestCountDetails {
  open?: number;
  pending?: number;
  approved?: number;
  declined?: number;
  cancelled?: number;
  timeout?: number;
  active?: number;
  completed?: number;
  expired?: number;
  lostByBalance?: number;
  lostByTimeout?: number;
}

export interface RequestCounts {
  bonus: number;
  deposit: number;
  withdraw: number;
  // Detailed breakdowns
  bonusDetails: RequestCountDetails;
  depositDetails: RequestCountDetails;
  withdrawDetails: RequestCountDetails;
}

/**
 * Creates a simple browser-compatible event system for managing request counts synchronization
 * Provides methods to subscribe, unsubscribe, and publish events across components
 * Used to keep request counts synchronized between different UI components
 */
const createCountsEventSystem = () => {
  
  const listeners = new Map<string, Set<(counts: RequestCounts) => void>>();
  let globalCounts: RequestCounts = { 
    bonus: 0, 
    deposit: 0, 
    withdraw: 0,
    bonusDetails: {},
    depositDetails: {},
    withdrawDetails: {}
  };

  return {

    /**
     * Subscribe to the event system
     * @param eventName - The name of the event to subscribe to
     * @param callback - The callback function to call when the event is published
     */
    subscribe: (eventName: string, callback: (counts: RequestCounts) => void) => {
      if (!listeners.has(eventName)) {
        listeners.set(eventName, new Set());
      }
      listeners.get(eventName)?.add(callback);
      // Immediately call with current counts
      callback(globalCounts);
    },

    /**
     * Unsubscribe from the event system
     * @param eventName - The name of the event to unsubscribe from
     * @param callback - The callback function to remove from the event
     */
    unsubscribe: (eventName: string, callback: (counts: RequestCounts) => void) => {
      const eventListeners = listeners.get(eventName);
      if (eventListeners) {
        eventListeners.delete(callback);
      }
    },

    /**
     * Publish an event to the event system
     * @param eventName - The name of the event to publish
     * @param newCounts - The new counts to publish
     */
    publish: (eventName: string, newCounts: RequestCounts) => {
      const eventListeners = listeners.get(eventName);
      globalCounts = newCounts;

      if (eventListeners) {
        eventListeners.forEach(callback => {
          try {
            callback(newCounts);
          }
          catch (error) {
            ConsoleLog.double(
              `🔴 ${CoreConfig.BackOfficeName} Error [Request Counts Sync]`,
              `Request counts sync error. Error: ${error}`,
              "error"
            );
          }
        });
      }
    },

    /**
     * Get the current counts
     * @returns The current counts
     */
    getCurrentCounts: () => globalCounts,

    /**
     * Update the counts
     * @param newCounts - The new counts to update
     */
    updateCounts: (newCounts: RequestCounts) => {
      globalCounts = newCounts;
    }
  };
};

// Global event system instance
const requestCountsEvents = createCountsEventSystem();

// Global interval management
let globalInterval: NodeJS.Timeout | null = null;
let subscriberCount = 0;

/**
 * Socket bridge state
 * ---
 * When the backoffice socket joins the client's `{clientSlug}_private` room,
 * the server pushes current counts via the `client_new_request_counts` event
 * and continues to emit on every change. While the socket is connected we do
 * not need to poll the count API — this flag short-circuits the fetch inside
 * the interval. The flag is owned by `RequestCountsSocketBridge` (set true on
 * successful room join, false on disconnect / error / missing clientSlug).
 */
let socketConnectedForCounts = false;

export const setSocketConnectedForCounts = (connected: boolean) => {
  socketConnectedForCounts = connected;
};

/**
 * Calculate total + detail breakdown from a raw counts payload (object or number).
 * Same shape handling as the count API response.
 */
const normalizeRequestCountsPayload = (raw: any): RequestCounts => {

  const calculateTotal = (data: any): number => {
    if (typeof data === 'number') {
      return data;
    }
    if (typeof data === 'object' && data !== null) {
      return Object.values(data).reduce((sum: number, val: any) => sum + (val || 0), 0);
    }
    return 0;
  };

  // Socket payloads may arrive either wrapped as `{ data: { bonus, deposit,
  // withdraw } }` (matching the count API response) or unwrapped at the top
  // level. Accept both shapes.
  const source = (raw && typeof raw === 'object' && raw.data && typeof raw.data === 'object')
    ? raw.data
    : raw;

  return {
    bonus           : calculateTotal(source?.bonus),
    deposit         : calculateTotal(source?.deposit),
    withdraw        : calculateTotal(source?.withdraw),
    bonusDetails    : typeof source?.bonus === 'object' && source?.bonus !== null ? source.bonus : {},
    depositDetails  : typeof source?.deposit === 'object' && source?.deposit !== null ? source.deposit : {},
    withdrawDetails : typeof source?.withdraw === 'object' && source?.withdraw !== null ? source.withdraw : {}
  };

};

/**
 * Publish counts received from a socket push into the global event bus.
 * Called by RequestCountsSocketBridge.
 */
export const publishRequestCountsFromSocket = (payload: any) => {
  const newCounts = normalizeRequestCountsPayload(payload);
  requestCountsEvents.publish('request-counts-update', newCounts);
};

/**
 * useRefetchOnCountChange
 * ---
 * Auto-refetches a finance table whenever the "new request" count it cares
 * about changes. The consuming table tells us which bucket to watch (e.g.
 * `withdrawDetails.open`) via a `selector`; whenever the selected value
 * differs from the previous tick, `refetch` is called.
 *
 * Replaces the per-table 20s `setInterval` blind refetch. Previously the
 * badge updated from the socket push instantly while the table waited up
 * to 20s, leaving the two out of sync. This hook closes that gap by
 * piggy-backing on the same socket-driven counts pipeline.
 *
 * Notes:
 * - `requestCountsEvents.subscribe` invokes the handler once with the
 *   current `globalCounts` on subscription. We treat this as a seed and
 *   skip the refetch on the very first call (the table runs its own fetch
 *   at mount, so a duplicate request is wasteful).
 * - `selector` and `refetch` are kept in refs so the underlying subscribe/
 *   unsubscribe doesn't churn on every parent render.
 *
 * @param selector Extracts the single number to watch from the counts state
 * @param refetch  Fired whenever the selected value changes
 */
export const useRefetchOnCountChange = (
  selector: (counts: RequestCounts) => number,
  refetch: () => void
) => {

  const selectorRef = useRef(selector);
  const refetchRef  = useRef(refetch);

  selectorRef.current = selector;
  refetchRef.current  = refetch;

  const previousValueRef = useRef<number | null>(null);

  useEffect(() => {

    const handler = (newCounts: RequestCounts) => {
      const currentValue = selectorRef.current(newCounts);

      if (previousValueRef.current !== null && currentValue !== previousValueRef.current) {
        refetchRef.current();
      }

      previousValueRef.current = currentValue;
    };

    requestCountsEvents.subscribe('request-counts-update', handler);

    return () => {
      requestCountsEvents.unsubscribe('request-counts-update', handler);
    };

  }, []);

};

// Track which pages want audio notifications
const audioSubscribers = new Set<string>();
const browserNotificationSubscribers = new Set<string>();

// Global audio context and state management for Opera compatibility
let audioContext: AudioContext | null = null;
let audioInitialized = false;
let userHasInteracted = false;

// Initialize audio context on first user interaction (for Opera compatibility)
const initializeAudio = () => {
  if (!audioInitialized && !audioContext) {
    try {
      audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      audioInitialized = true;
      
      // Resume audio context if suspended (required by some browsers)
      if (audioContext.state === 'suspended') {
        audioContext.resume();
      }
    } catch (error) {
      ConsoleLog.double(
        `🔴 ${CoreConfig.BackOfficeName} Error [Audio Initialization]`,
        `Could not initialize audio context. Error: ${error}`,
        "error"
      );
    }
  }
};

// Detect Opera browser
const isOpera = () => {
  return (navigator.userAgent.indexOf('OPR/') > -1) || 
         (navigator.userAgent.indexOf('Opera') > -1);
};

// Add global click listener to enable audio and notifications (Opera requirement)
if (typeof window !== 'undefined') {
  const enableAudioAndNotificationsOnInteraction = () => {
    if (!userHasInteracted) {
      userHasInteracted = true;
      initializeAudio();
      
      // Try to play a silent sound to "unlock" audio
      try {
        const audio = new Audio();
        audio.src = "data:audio/wav;base64,UklGRigAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=";
        audio.volume = 0.01;
        audio.play().catch(() => {}); // Silent fail is ok
      } catch (error) {
        // Silent fail
      }
      
      // For Opera, also request notification permission on first interaction
      if (isOpera() && "Notification" in window && Notification.permission === "default") {
        Notification.requestPermission().then(permission => {
          ConsoleLog.double(
            `✅ ${CoreConfig.BackOfficeName}  Info [Notification Permission]`,
            `Opera notification permission: ${permission}`,
            "info"
          );
        }).catch(error => {
          ConsoleLog.double(
            `⚠️ ${CoreConfig.BackOfficeName} Warning [Notification Permission]`,
            `Could not request notification permission in Opera: ${error}`,
            "warning"
          );
        });
      }
      
      document.removeEventListener('click', enableAudioAndNotificationsOnInteraction);
      document.removeEventListener('keydown', enableAudioAndNotificationsOnInteraction);
      document.removeEventListener('touchstart', enableAudioAndNotificationsOnInteraction);
    }
  };

  document.addEventListener('click', enableAudioAndNotificationsOnInteraction);
  document.addEventListener('keydown', enableAudioAndNotificationsOnInteraction);
  document.addEventListener('touchstart', enableAudioAndNotificationsOnInteraction);
}

// Audio notification function - works in background tabs
const playNotificationSound = () => {
  
  try {
    
    // For Opera, ensure user has interacted first
    if (isOpera() && !userHasInteracted) {
      ConsoleLog.double(
        `⚠️ ${CoreConfig.BackOfficeName} Warning [Notification Sound]`,
        "Opera requires user interaction before playing audio. Please click anywhere on the page first.",
        "warning"
      );
      return;
    }

    // Method 1: Try HTML5 Audio (works better in background tabs)
    const audio = new Audio();
    
    // Use a more compatible WAV format for Opera
    const audioData = isOpera() 
      ? "data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmEaCDCH0fDMeTEA"
      : "data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmEaCDCHfDMeTEA";
    
    audio.src = audioData;
    audio.volume = 0.3;
    
    // For Opera, try to ensure audio context is active
    if (isOpera() && audioContext && audioContext.state === 'suspended') {
      audioContext.resume().then(() => {
        audio.play().catch(handleAudioError);
      }).catch(handleAudioError);
    }
    else {
      // Try to play
      const playPromise = audio.play();
      
      if (playPromise !== undefined) {
        playPromise.catch(handleAudioError);
      }
    }
    
    // Fallback function for audio errors
    function handleAudioError(error: any) {
      // If HTML5 audio fails, try Web Audio API as fallback
      try {
        const context = audioContext || new (window.AudioContext || (window as any).webkitAudioContext)();
        
        // Resume context if suspended
        if (context.state === 'suspended') {
          context.resume();
        }
        
        const oscillator = context.createOscillator();
        const gainNode = context.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(context.destination);
        
        // Different frequency for Opera to ensure compatibility
        oscillator.frequency.value = isOpera() ? 750 : 800;
        oscillator.type = 'sine';
        
        gainNode.gain.setValueAtTime(0.3, context.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, context.currentTime + 0.5);
        
        oscillator.start(context.currentTime);
        oscillator.stop(context.currentTime + 0.5);
        
        // Store context for reuse
        if (!audioContext) {
          audioContext = context;
        }
      }
      catch (webAudioError) {
        ConsoleLog.double(
          `🔴 ${CoreConfig.BackOfficeName} Error [Notification Sound]`,
          `Could not play notification sound with any method. Browser: ${isOpera() ? 'Opera' : 'Other'}, HTML5 Error: ${error}, Web Audio Error: ${webAudioError}`,
          "error"
        );
      }
    }
    
  } catch (error) {
    ConsoleLog.double(
      `🔴 ${CoreConfig.BackOfficeName} Error [Notification Sound]`,
      `Could not play notification sound. Browser: ${isOpera() ? 'Opera' : 'Other'}, Error: ${error}`,
      "error"
    );
  }
};

// Browser notification function - works even when tab is not active
const showBrowserNotification = (counts: RequestCounts) => {
  // Check if notifications are supported
  if (!("Notification" in window)) {
    ConsoleLog.double(
      `⚠️ ${CoreConfig.BackOfficeName} Warning [Browser Notification]`,
      "Browser notifications are not supported in this browser.",
      "warning"
    );
    return;
  }

  // For Opera, ensure user has interacted first for notifications too
  if (isOpera() && !userHasInteracted) {
    ConsoleLog.double(
      `⚠️ ${CoreConfig.BackOfficeName} Warning [Browser Notification]`,
      "Opera requires user interaction before showing notifications. Please click anywhere on the page first.",
      "warning"
    );
    return;
  }

  // Check permission and request if needed
  if (Notification.permission === "granted") {

    const totalCounts = counts.deposit + counts.withdraw + counts.bonus;
    
    if (totalCounts > 0) {
      const notifications = [];
      
      if (counts.deposit > 0)   notifications.push(globalTranslateInstance("forms.notifications.newDeposit", { count: counts.deposit }));
      
      // Withdraw notifications with detailed breakdown
      if (counts.withdrawDetails?.open && counts.withdrawDetails.open > 0) {
        notifications.push(globalTranslateInstance("forms.notifications.newWithdrawOpen", { count: counts.withdrawDetails.open }));
      }
      if (counts.withdrawDetails?.pending && counts.withdrawDetails.pending > 0) {
        notifications.push(globalTranslateInstance("forms.notifications.newWithdrawPending", { count: counts.withdrawDetails.pending }));
      }
      
      if (counts.bonus > 0)     notifications.push(globalTranslateInstance("forms.notifications.newBonus", { count: counts.bonus }));
      
      const title = globalTranslateInstance("forms.notifications.newFinanceRequestTitle");
      const body  = notifications.join(", ") + " " + globalTranslateInstance("forms.notifications.hasNewRequest");
      
      // Opera-specific notification options
      const notificationOptions = {
        body                : body,
        icon                : Asset("logos.Favicon"),
        badge               : Asset("logos.Favicon"),
        tag                 : "request-counts", // Prevents multiple notifications
        requireInteraction  : isOpera() ? true : false, // Opera works better with user interaction
        silent              : false, // This allows sound even in background
        ...(isOpera() && {
          // Opera-specific enhancements
          renotify: true,
          vibrate: [200, 100, 200]
        })
      };
      
      try {
        const notification = new Notification(title, notificationOptions);
        
        // Auto close after 5 seconds (longer for Opera to ensure visibility)
        setTimeout(() => {
          notification.close();
        }, isOpera() ? 8000 : 5000);
        
        // Optional: Focus window when clicked
        notification.onclick = () => {
          window.focus();
          notification.close();
        };

      } catch (error) {
        ConsoleLog.double(
          `🔴 ${CoreConfig.BackOfficeName} Error [Browser Notification]`,
          `Failed to show notification. Browser: ${isOpera() ? 'Opera' : 'Other'}, Error: ${error}`,
          "error"
        );
      }

    }

  }
  else if (Notification.permission !== "denied") {

    // For Opera, don't request permission automatically - it was handled in user interaction
    if (isOpera() && !userHasInteracted) {
      ConsoleLog.double(
        `⚠️ ${CoreConfig.BackOfficeName} Warning [Browser Notification]`,
        "Opera notification permission will be requested on first user interaction.",
        "warning"
      );
      return;
    }

    // Request permission (non-Opera browsers or after user interaction)
    Notification.requestPermission().then(permission => {
      if (permission === "granted") {
        showBrowserNotification(counts);
      }
    }).catch(error => {
      ConsoleLog.double(
        `🔴 ${CoreConfig.BackOfficeName} Error [Browser Notification]`,
        `Failed to request notification permission. Browser: ${isOpera() ? 'Opera' : 'Other'}, Error: ${error}`,
        "error"
      );
    });

  } else {
    ConsoleLog.double(
      `⚠️ ${CoreConfig.BackOfficeName} Warning [Browser Notification]`,
      `Notifications are denied by user. Browser: ${isOpera() ? 'Opera' : 'Other'}`,
      "warning"
    );
  }

};

// Global API fetching function
let globalAPIInstance       : any = null;
let globalTranslateInstance : any = null;

/**
 * Initialize the global fetcher
 * @param _fetch - The fetch function
 * @param t - The translation function
 */
const initializeGlobalFetcher = (_fetch: any, t: any) => {
  globalAPIInstance = _fetch;
  globalTranslateInstance = t;
};

/**
 * Load the counts globally
 */
const loadCountsGlobally = () => {
  
  if (!globalAPIInstance || !globalTranslateInstance) return;
  
  const REQUEST_COUNTS_API_PATH = `${CoreConfig.API.backoffice.Payment}/client/read/request/new/count`;
  
  const onError = (error?: INNError) => {
    toast.error(
      globalTranslateInstance("texts.error"),
      {
        description: globalTranslateInstance("tables.notifications.requestCountsLoadFailed"),
      }
    );
    if (error) {
      ConsoleLog.double(
        `🔴 ${CoreConfig.BackOfficeName} Error [Request Counts]`,
        "Can't read request counts! " + error?.detail,
        "error"
      );
    }
  };

  /**
   * Fetch the counts
   */
  globalAPIInstance({
    apiPath: REQUEST_COUNTS_API_PATH,
    success: (result: any) => {
      if (result.success) {
        // Reuse the same normalizer the socket bridge uses to keep
        // polling and push paths in sync.
        const newCounts = normalizeRequestCountsPayload(result);

        // Update global state and notify all subscribers
        requestCountsEvents.publish('request-counts-update', newCounts);
      } else {
        onError();
      }
    },
    error: onError
  });
};

/**
 * Notification options
 */
interface NotificationOptions {
  enableAudio?: boolean;
  enableBrowserNotification?: boolean;
  pageId?: string; // Unique identifier for the page/component
}

/**
 * Custom hook for managing request counts across different components
 * Provides global state management, automatic synchronization, and audio notifications
 * @param notificationOptions - Options for notifications (audio and browser notifications)
 * @param intervalMs - Interval in milliseconds for automatic refresh (default: 10000)
 * @param enabled - Whether the hook should fetch data (default: true)
 * @returns Object with counts, loading state, and manual refresh function
 */
export const useRequestCountsSync = (
  notificationOptions: NotificationOptions = { enableAudio: false, enableBrowserNotification: false },
  intervalMs: number = 10000, 
  enabled: boolean = true
) => {
  
  const { t } = useTranslation();
  const { _fetch } = useAPI();
  
  const [counts, setCounts] = useState<RequestCounts>({
    bonus: 0,
    deposit: 0,
    withdraw: 0,
    bonusDetails: {},
    depositDetails: {},
    withdrawDetails: {}
  });
  const previousCountsRef = useRef<RequestCounts>({
    bonus: 0,
    deposit: 0,
    withdraw: 0,
    bonusDetails: {},
    depositDetails: {},
    withdrawDetails: {}
  });

  /**
   * The very first `handleCountsUpdate` call is the immediate-callback that
   * `requestCountsEvents.subscribe` fires synchronously with the current
   * `globalCounts` (a seed, not a real change). We hold this flag false on
   * that first tick so the seed isn't mistaken for a "new request" and
   * does not trigger a notification; every subsequent update flips the
   * flag to true and runs through the normal notification logic.
   *
   * This replaces the old `(previous total > 0)` gate, which suppressed
   * notifications whenever operators had just cleared the queue back to
   * zero — the next genuinely new request was missed. With the seed flag
   * we get notifications on the first real request regardless of the
   * previous total.
   */
  const hasSeededRef = useRef(false);

  // Initialize global fetcher
  useEffect(() => {
    initializeGlobalFetcher(_fetch, t);
  }, [_fetch, t]);

  // Listen for count updates from other components
  useEffect(() => {
    // Don't start if not enabled
    if (!enabled) {
      return;
    }

    const eventName = 'request-counts-update';
    
    const handleCountsUpdate = (newCounts: RequestCounts) => {
      const previous = previousCountsRef.current;
      const hasIncrease =
        newCounts.bonus > previous.bonus ||
        newCounts.deposit > previous.deposit ||
        newCounts.withdraw > previous.withdraw;

      /**
       * Fire a notification on every real increase except the seed call.
       * The previous implementation also required `(previous total > 0)`,
       * which suppressed alerts the moment operators emptied the queue
       * back to zero. Treating only the mount-time immediate-callback as
       * a seed lets every genuine delta — including 0 → 1 — through.
       */
      if (hasIncrease && hasSeededRef.current) {
        if (audioSubscribers.size > 0) {
          playNotificationSound();
        }
        if (browserNotificationSubscribers.size > 0) {
          showBrowserNotification(newCounts);
        }
      }

      hasSeededRef.current = true;
      previousCountsRef.current = newCounts;
      setCounts(newCounts);
    };

    // Increment subscriber count
    subscriberCount++;
    requestCountsEvents.subscribe(eventName, handleCountsUpdate);

    // Register notification preferences for this page
    const pageId = notificationOptions.pageId || `page-${Date.now()}-${Math.random()}`;
    
    if (notificationOptions.enableAudio) {
      audioSubscribers.add(pageId);
    }
    
    if (notificationOptions.enableBrowserNotification) {
      browserNotificationSubscribers.add(pageId);
    }

    // Start global interval if this is the first subscriber
    if (subscriberCount === 1) {
      /**
       * Only fetch immediately when the socket isn't connected. When the
       * socket has joined the `{clientSlug}_private` room the server
       * already pushes the current snapshot, so an extra API call here
       * would be redundant.
       */
      if (!socketConnectedForCounts) {
        loadCountsGlobally();
      }

      /**
       * Always install the interval; the callback skips the fetch while
       * the socket is connected and falls back to polling once it drops.
       */
      globalInterval = setInterval(() => {
        if (socketConnectedForCounts) return;
        loadCountsGlobally();
      }, intervalMs);
    }

    return () => {
      // Decrement subscriber count
      subscriberCount--;
      requestCountsEvents.unsubscribe(eventName, handleCountsUpdate);
      
      // Unregister notification preferences for this page
      audioSubscribers.delete(pageId);
      browserNotificationSubscribers.delete(pageId);
      
      // Stop global interval if no more subscribers
      if (subscriberCount === 0 && globalInterval) {
        clearInterval(globalInterval);
        globalInterval = null;
      }
    };
    
  }, [
    notificationOptions.enableAudio,
    notificationOptions.enableBrowserNotification,
    notificationOptions.pageId,
    intervalMs,
    enabled,
    t
  ]);

  // Manual refresh function
  const refreshCounts = useCallback(() => {
    if (enabled) {
      loadCountsGlobally();
    }
  }, [enabled]);

  return {
    counts,
    refreshCounts
  };
}; 